<template>
  <div class="contact-review card p-4 mb-3" novalidate>
    <div v-if="accountFields.length > 0">
      <h3 class="mb-1 mb-sm-4">
        {{ $t("signup.contact_review_component.signer_title") }}
      </h3>
      <!-- Account Data Form -->
      <div class="row mb-2 g-2" v-for="field in accountFields" :key="field.id">
        <label :for="field.id" class="col-sm-3 mt-sm-3 col-form-label">{{
          field.label
        }}</label>
        <div class="col-sm-8 col-10">
          <PhoneNumberInput
            v-if="field.model === 'phone_number'"
            :id="field.id"
            :modelValue="accountData[field.model]"
            :countryCode="phoneCountryCode"
            :invalid="v$.accountData[field.model].$error"
            @update:modelValue="updateAccountValue(field.model, $event)"
            @blur="v$.accountData[field.model].$touch"
          />
          <input
            v-else
            :type="field.type"
            :id="field.id"
            class="form-control"
            :class="{
              'is-invalid': v$.accountData[field.model].$error,
            }"
            :disabled="!field.editable"
            :value="accountData[field.model]"
            @input="updateAccountField(field.model, $event)"
            @blur="v$.accountData[field.model].$touch"
          />
          <div
            class="invalid-feedback"
            v-for="error of v$.accountData[field.model].$errors"
            :key="error.$uid"
          >
            {{ error.$message }}
          </div>
        </div>
        <div class="col-sm-1 col-2">
          <button
            class="btn btn-outline-secondary h-100"
            v-if="!field.editable"
            @click="enableEditing(field)"
          >
            <font-awesome-icon icon="pencil-alt" />
          </button>
        </div>
      </div>
    </div>

    <div v-if="shouldShowCompanyFields">
      <h3 class="mb-1 mb-sm-2 mt-4">
        {{
          company_section_title
            ? company_section_title
            : $t("signup.contact_review_component.generator_owner_title")
        }}
      </h3>
      <p class="mb-2 mb-sm-4">
        {{
          company_section_description
            ? company_section_description
            : $t("signup.contact_review_component.generator_owner_subtext")
        }}
      </p>
    </div>

    <div v-if="enableAutofill || isExistingCompanyWithDocuments">
      <CompanySelectDropdown
        :existingCompanies="existingCompanies"
        :selectedCompany="company"
        @selectExistingCompany="$emit('selectExistingCompany', $event)"
        @resetCompanySelection="$emit('resetCompanySelection', $event)"
      />
      <hr />
    </div>

    <div
      v-if="
        enableCompanySearch &&
        countryCode !== '' &&
        !isExistingCompanyWithDocuments
      "
    >
      <GlobalCompanySearchDropdown
        :countryCode="countryCode"
        @select="handleCompanyPicked"
      />
      <hr />
    </div>

    <!-- Company Data Form -->
    <div v-for="field in companyFields" :key="field.id">
      <div
        class="row mb-2 g-2"
        v-if="field.model === 'address' || shouldShowCompanyFields"
      >
        <label :for="field.id" class="col-sm-3 mt-sm-3 col-form-label">{{
          field.label
        }}</label>
        <div class="col-sm-8 col-10">
          <input
            :type="field.type"
            :id="field.id"
            class="form-control"
            :class="{ 'is-invalid': v$.company[field.model].$error }"
            :disabled="
              !field.editable ||
              (isExistingCompanyWithDocuments &&
                !existingDataOverride &&
                !field.alwaysEditable)
            "
            :value="company[field.model] ?? field.defaultValue"
            @input="updateCompanyField(field.model, $event)"
            @blur="v$.company[field.model].$touch"
          />
          <div
            class="invalid-feedback"
            v-for="error of v$.company[field.model].$errors"
            :key="error.$uid"
          >
            {{ error.$message }}
          </div>
        </div>
        <div class="col-sm-1 col-2">
          <button
            class="btn btn-outline-secondary h-100"
            v-if="!field.editable"
            @click="enableEditing(field)"
          >
            <font-awesome-icon icon="pencil-alt" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import GlobalCompanySearchDropdown from "@/components/common/GlobalCompanySearchDropdown.vue";
import PhoneNumberInput from "@/components/common/PhoneNumberInput.vue";
import { useCompanyFinder } from "@/composables/useCompanyFinder";
import { getDialCode } from "@/utils/countryDialCodes";
import { useSignupStore } from "@/signup/signup";
import type {
  Account,
  Company,
  ContactFieldType,
  PickedCompany,
} from "@/types";
import { useVuelidate } from "@vuelidate/core";
import { computed, reactive, toRefs } from "vue";
import CompanySelectDropdown from "./CompanySelectDropdown.vue";

const props = withDefaults(
  defineProps<{
    accountData: Account;
    companyIndex?: number;
    editableAccountFields: ContactFieldType[];
    editableCompanyFields: ContactFieldType[];
    shouldShowCompanyFields?: boolean;
    enableAutofill?: boolean;
    existingCompanies?: Company[];
    enableCompanySearch?: boolean;
    countryCode?: string;
    company_section_title?: string;
    company_section_description?: string;
    existingDataOverride?: boolean;
  }>(),
  {
    shouldShowCompanyFields: true,
    enableAutofill: false,
    existingCompanies: () => [],
    enableCompanySearch: false,
    countryCode: "",
    company_section_title: "",
    company_section_description: "",
    existingDataOverride: false,
  }
);

const emit = defineEmits<{
  updateAccountData: [accountData: Account];
  handleUpdateCompany: [company: Company];
  selectExistingCompany: [companyId: number];
  // CompanySelectDropdown emits this without a payload, but the template forwards its `$event`
  // on to the parent. `undefined` keeps that forwarding type-checkable while still rejecting a
  // real value; a zero-arity signature would reject the forwarding itself.
  resetCompanySelection: [payload?: undefined];
}>();

const { selectedCompany } = useCompanyFinder(props.accountData);
const company = computed(() => selectedCompany.value);
const { accountData } = toRefs(props);

// Seed the phone dial-code from the explicit prop when given (e.g. PL/ES/EE/LV/LT/SE pass a
// fixed country code), otherwise fall back to the flow's selected country, and finally the
// in-progress company's country. The default flow passes the "default" sentinel when no
// ?country= is set, so we only accept a candidate that actually resolves to a dial code and
// skip sentinels ("default", "XX", ...) that would otherwise block the fallback.
const signupStore = useSignupStore();
const phoneCountryCode = computed(
  () =>
    [props.countryCode, signupStore.country, company.value?.country].find(
      (code) => code && getDialCode(code)
    ) || ""
);

const accountFields: ContactFieldType[] = reactive(props.editableAccountFields);
const companyFields: ContactFieldType[] = reactive(props.editableCompanyFields);

const enableEditing = (field: { ["editable"]: boolean }) => {
  field.editable = true;
};

// FORM VALIDATION SECTION

const rules = computed(() => {
  const validationRules = { accountData: {}, company: {} } as any;

  // Rules for account fields
  accountFields.forEach((field) => {
    validationRules.accountData[field.model] = field.validators;
  });

  // Rules for company fields
  companyFields.forEach((field) => {
    validationRules.company[field.model] = field.validators;
  });

  return validationRules;
});

const v$ = useVuelidate(rules, { accountData, company });

// Value-based updater for custom inputs (e.g. PhoneNumberInput) that emit a string rather
// than a DOM event.
const updateAccountValue = (field: string | number, value: string) => {
  const updatedValue = {
    ...props.accountData,
    [field]: value,
  } as Account;
  // Emit an event to update the parent's subsidy object
  emit("updateAccountData", updatedValue);
};
const updateAccountField = async (field: string | number, event: Event) => {
  updateAccountValue(field, (event.target as HTMLInputElement).value);
};
const updateCompanyField = async (field: string | number, event: Event) => {
  const updatedValue = {
    ...company.value,
    [field]: (event.target as HTMLInputElement).value,
  } as Company;
  // Emit an event to update the parent's subsidy object
  emit("handleUpdateCompany", updatedValue);
};

// GlobalCompanySearchDropdown emits a registry-search result whose `id` is a registry id,
// not an account Company.id. Letting it overwrite the in-progress company's id breaks the
// empty-company tracking (companyIndex resolves to -1), which collapses the whole contact
// form. Keep the local company's own id; take the rest of the match as autofill.
// The autofill patch only carries the registry-search fields, so it is a subset of Company.
// Partial<Company> would be the honest payload type, but changing it is an API change rather
// than part of a script-setup conversion — one parent declares this emit as Company and the
// rest forward it through untyped `emits` arrays. Widen here and leave the contract alone.
const handleCompanyPicked = (picked: PickedCompany) => {
  emit("handleUpdateCompany", {
    ...picked,
    id: company.value?.id,
  } as unknown as Company);
};

const isExistingCompanyWithDocuments = computed(() => {
  return (
    company.value &&
    company.value.id !== undefined &&
    company.value.documents &&
    company.value.documents.length > 0
  );
});
</script>

<style scoped lang="scss">
.contact-review {
  .col-form-label {
    text-align: left;
    font-weight: bold;
    padding: 0;
  }
  .btn-outline-secondary {
    padding: 0.375rem 0.75rem;
  }
  .row {
    margin-left: 0;
    margin-right: 0;
  }
}

@media (max-width: 575.98px) {
  .contact-review {
    .col-form-label {
      margin-top: 1rem;
    }
  }
  h3 {
    font-size: 1.2rem;
  }
}
</style>
